import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { checkGameResult, chooseQuestionVariant } from '../utils/gameUtils';
import { QUESTION_TYPES } from '../utils/constants';

/**
 * =====================================================================
 * GamePlay Component
 * =====================================================================
 *
 * A minimalistic, clean interface for the Acquaintance Game.
 *
 * Key Features:
 * - Clean, uncluttered question display with instant visibility
 * - Minimalist design with no animations
 * - Work name and question always visible
 * - Simple, focused interaction model (submit button only)
 * - Hint system for guidance
 *
 * @param {Object} props - Component props containing game state and handlers
 * @returns {JSX.Element} The gameplay interface component
 */
const GamePlay = ({ 
  currentWork,
  questions,
  currentQuestionIndex,
  setCurrentQuestionIndex,
  userAnswers,
  setUserAnswers,
  askedQuestions,
  setAskedQuestions,
  inputValue,
  setInputValue,
  setGameResult,
  setGameOver,
  setDebugInfo,
  subject,
  markWorkAsCompleted,
  setGameFailureReason
}) => {
  // =====================================================================
  // STATE MANAGEMENT
  // =====================================================================

  /**
   * DOM references for focus management
   */
  const inputRef = useRef(null);         // Reference to the answer input field

  /**
   * UI state for interaction tracking
   */
  const [inputError, setInputError] = useState(false);           // Tracks input validation errors
  const [isSubmitting, setIsSubmitting] = useState(false);       // Tracks submission state for UI feedback
  const [errorMessage, setErrorMessage] = useState('');          // Stores error message for display
  
  /**
   * Hint system state management
   * Provides contextual assistance
   */
  const [isHintVisible, setIsHintVisible] = useState(false);         // Controls visibility of question-specific hints
  const [isOverallHintVisible, setIsOverallHintVisible] = useState(false); // Controls visibility of work-level hints
  
  /**
   * Configuration constants
   * Centralized for easy adjustment
   */
  const maxQuestions = 4;       // Fixed number of questions per game session
  
  /**
   * Creates a limited array of exactly 4 questions
   * Ensures consistent gameplay regardless of source data
   * Memoized to prevent unnecessary recalculation
   */
  const limitedQuestions = React.useMemo(() => {
    return questions.slice(0, maxQuestions);
  }, [questions]);
  
  // =====================================================================
  // EFFECT HOOKS
  // =====================================================================
  
  /**
   * Focus effect for question transitions
   * Runs when the current question changes
   */
  useEffect(() => {
    // Focus input field immediately
    if (inputRef.current) {
      inputRef.current.focus();
    }

    // Reset hint visibility on question change for clean state
    setIsHintVisible(false);
    setIsOverallHintVisible(false);
  }, [currentQuestionIndex, limitedQuestions, currentWork]);
  
  // =====================================================================
  // UTILITY FUNCTIONS
  // =====================================================================
  
  /**
   * Provides haptic feedback for enhanced physical interaction
   * Uses the Vibration API when available on device
   * 
   * @param {string} type - Type of feedback pattern ('success', 'error', or 'light')
   */
  const simulateHapticFeedback = (type) => {
    // Skip if vibration API is not available
    if (!navigator.vibrate) return;
    
    // Define vibration patterns tuned for different feedback types
    switch(type) {
      case 'success':
        navigator.vibrate([10, 30, 10]); // Double-pulse pattern
        break;
      case 'error':
        navigator.vibrate([50, 20, 50]); // Stronger, attention-grabbing pattern
        break;
      case 'light':
        navigator.vibrate(10); // Subtle feedback for basic interactions
        break;
      default:
        navigator.vibrate(15); // Default gentle feedback
    }
  };

  // =====================================================================
  // NAVIGATION & QUESTION FLOW
  // =====================================================================

  /**
   * Handles transition between questions
   * Simply updates state without animations
   *
   * @returns {void}
   */
  const moveToNextQuestion = () => {
    const nextIndex = currentQuestionIndex + 1;

    // Check if we've reached the end of questions
    if (nextIndex >= maxQuestions) {
      finalizeGame();
      return;
    }

    // Get next question information
    const nextQuestion = limitedQuestions[nextIndex];
    const nextQuestionType = nextQuestion.type;

    // Update game state for next question
    setAskedQuestions(prev => {
      const updatedSet = new Set([...prev]);
      updatedSet.add(nextQuestionType);
      return updatedSet;
    });

    // Update UI state for next question
    setCurrentQuestionIndex(nextIndex);
    setInputValue('');
  };

  /**
   * Finalizes the game and evaluates results
   * Processes all answers at once rather than individually
   * 
   * @returns {void}
   */
  const finalizeGame = () => {
    // Ensure final answer is captured before evaluation
    const currentQuestion = limitedQuestions[currentQuestionIndex];
    const finalAnswers = {
      ...userAnswers,
      [currentQuestion.type]: inputValue.trim()
    };
    
    // Validate all answers at once
    const result = checkGameResult(limitedQuestions, currentWork, finalAnswers, askedQuestions, subject);
    setDebugInfo(result.debugInfo);
    
    if (result.isCorrect) {
      // Success path - all answers correct
      markWorkAsCompleted(
        currentWork.work, 
        true, 
        result.correctAnswers, 
        result.answeredQuestions
      );
      setGameResult(true);
    } else {
      // Failure path - at least one incorrect answer
      markWorkAsCompleted(
        currentWork.work, 
        false, 
        result.correctAnswers, 
        result.answeredQuestions
      );
      setGameFailureReason("Please try again");
      setGameResult(false);
    }
    
    // Trigger transition to results screen
    setGameOver(true);
  };

  // =====================================================================
  // USER INTERACTIONS
  // =====================================================================

  /**
   * Processes user answer submission
   * Validates input and advances game flow
   *
   * @param {Event} e - Optional form submission event
   * @returns {void}
   */
  const handleAnswerSubmit = (e) => {
    // Prevent default form submission
    if (e && e.preventDefault) {
      e.preventDefault();
    }

    // Activate submission visual state
    setIsSubmitting(true);

    // Get current question information
    const currentQuestion = limitedQuestions[currentQuestionIndex];
    const questionType = currentQuestion.type;

    // Sanitize user input
    const userAnswer = inputValue.trim();

    // Validate non-empty answer
    if (!userAnswer) {
      // Error path - empty answer
      setInputError(true);
      setErrorMessage('Please enter an answer');
      simulateHapticFeedback('error');
      setIsSubmitting(false);

      // Maintain focus on input for correction
      if (inputRef.current) {
        inputRef.current.focus();
      }
      return;
    }

    // Clear any previous error state
    setInputError(false);
    setErrorMessage('');

    // Store answer without validation (collect only)
    setUserAnswers(prevAnswers => ({
      ...prevAnswers,
      [questionType]: userAnswer
    }));

    // Provide tactile feedback
    simulateHapticFeedback('light');

    // Advance to next question or finalize
    setIsSubmitting(false);

    if (currentQuestionIndex < maxQuestions - 1) {
      moveToNextQuestion();
    } else {
      finalizeGame();
    }
  };

  // =====================================================================
  // INPUT HANDLING
  // =====================================================================

  /**
   * Processes and sanitizes input changes
   * Applies type-specific validation rules
   * 
   * @param {Event} e - The input change event
   * @returns {void}
   */
  const handleInputChange = (e) => {
    let newValue = e.target.value;
    
    // Get current question type for specific validation
    const questionType = limitedQuestions[currentQuestionIndex]?.type;
    
    // Clear error when user starts typing
    if (newValue && inputError) {
      setInputError(false);
    }
    
    // Apply input sanitization based on question type
    if (questionType === QUESTION_TYPES.PUBLICATION_YEAR) {
      // For years, restrict to numeric characters, hyphens, and spaces
      newValue = newValue.replace(/[^0-9\-\s]/g, '');
    } else {
      // For text answers, allow alphanumeric plus common punctuation
      newValue = newValue.replace(/[^\w\s.,'-]/g, '');
    }
    
    // Provide subtle haptic feedback when adding characters
    if (newValue.length > inputValue.length) {
      simulateHapticFeedback('light');
    }
    
    // Update input with sanitized value
    setInputValue(newValue);
  };

  // =====================================================================
  // HINT SYSTEM
  // =====================================================================

  /**
   * Toggles visibility of question-specific hints
   *
   * @returns {void}
   */
  const toggleHint = () => {
    // Toggle hint visibility
    setIsHintVisible(!isHintVisible);

    // Provide haptic feedback
    if (!isHintVisible) {
      simulateHapticFeedback('light');
    }
  };
  
  /**
   * Toggles visibility of work-level hints
   * Provides broader context about the literary work
   *
   * @returns {void}
   */
  const toggleOverallHint = () => {
    // Toggle overall hint visibility
    setIsOverallHintVisible(!isOverallHintVisible);

    // Provide haptic feedback
    if (!isOverallHintVisible) {
      simulateHapticFeedback('success');
    }
  };

  /**
   * Retrieves the hint for the current question type
   * Maps question types to corresponding hint fields
   * 
   * @returns {string} The hint text for the current question
   */
  const getCurrentHint = () => {
    if (!currentWork) return "No hint available";
    
    // Get current question type
    const currentQuestion = limitedQuestions[currentQuestionIndex];
    const questionType = currentQuestion?.type;
    
    // Map question types to hint fields
    const hintMapping = {
      [QUESTION_TYPES.AUTHOR]: 'author_hint',
      [QUESTION_TYPES.PUBLICATION_YEAR]: 'publication_year_hint',
      [QUESTION_TYPES.GENRE]: 'genre_hint', 
      [QUESTION_TYPES.LITERARY_AGE]: 'age_hint'
    };
    
    // Get appropriate hint field
    const hintField = hintMapping[questionType];
    
    // Return hint if available
    return currentWork[hintField] || `No hint available for ${questionType}`;
  };
  
  /**
   * Retrieves the overall hint for the current work
   * 
   * @returns {string} The overall hint text
   */
  const getOverallHint = () => {
    if (!currentWork || !currentWork.overall_hint) {
      return "No overall hint available for this work.";
    }
    
    return currentWork.overall_hint;
  };

  // =====================================================================
  // RENDERING HELPERS
  // =====================================================================
  
  // Get current question information
  const currentQuestion = limitedQuestions[currentQuestionIndex];
  const questionType = currentQuestion?.type;
  
  // Determine input type based on question type
  const isPublicationYearQuestion = questionType === QUESTION_TYPES.PUBLICATION_YEAR;
  
  /**
   * Generates a minimalist question text
   * Creates concise, elegant questions
   * 
   * @returns {string} The formatted question text
   */
  const getQuestionText = () => {
    // Show loading state if questions aren't ready
    if (!limitedQuestions || limitedQuestions.length === 0 || currentQuestionIndex >= limitedQuestions.length) {
      return "Loading...";
    }
    
    // Get current question and return minimalistic variant
    return chooseQuestionVariant(limitedQuestions[currentQuestionIndex], true);
  };
  
  /**
   * Determines if the work title needs marquee animation
   * Based on title length threshold
   * 
   * @returns {boolean} Whether marquee should be applied
   */
  const needsMarquee = () => {
    const title = currentWork.work || "";
    return title.length > 20; // Threshold for marquee activation
  };
  
  /**
   * Formats the work title for marquee display
   * Adds spacing for smoother scrolling effect
   * 
   * @returns {string} The formatted title for display
   */
  const getMarqueeTitle = () => {
    const title = currentWork.work || "Loading...";
    
    // Add padding for smoother scrolling on long titles
    if (needsMarquee()) {
      return `${title}   `;
    }
    
    return title;
  };

  // =====================================================================
  // COMPONENT RENDER
  // =====================================================================
  return (
    <div className="game-container">
      {/* Title bar with work name and progress indicator */}
      <div className="work-title">
        <div className={`marquee-container ${needsMarquee() ? 'needs-marquee' : 'no-marquee'}`}>
          <h2 className="marquee-text">{getMarqueeTitle()}</h2>
        </div>
        <div className="progress-indicator">
          Question {currentQuestionIndex + 1} of 4
        </div>
      </div>

      {/* Main question container */}
      <div className="question-container">
        {/* Question display */}
        <div className="question-minimalist">
          {getQuestionText()}
        </div>

        {/* Answer form */}
        <form
          className={`answer-form ${isSubmitting ? 'form-submitting' : ''}`}
          onSubmit={handleAnswerSubmit}
        >
          <div className={`answer-input ${inputError ? 'input-error' : ''}`}>
            {/* Input field */}
            <input
              ref={inputRef}
              type="text"
              placeholder={isPublicationYearQuestion ? "Enter year or range" : "Your answer..."}
              value={inputValue}
              onChange={handleInputChange}
              autoComplete="off"
              autoCapitalize="words"
              spellCheck="false"
              inputMode={isPublicationYearQuestion ? "numeric" : "text"}
              aria-label={`Answer for ${getQuestionText()}`}
              disabled={isSubmitting}
            />

            {/* Error message display */}
            {inputError && (
              <div className="error-message">
                {errorMessage}
              </div>
            )}

            {/* Submit button */}
            <button
              type="submit"
              className={`submit-button ${isSubmitting ? 'submitting' : ''}`}
              disabled={isSubmitting}
            >
              Submit
            </button>
          </div>
        </form>

        {/* Hint system controls */}
        <div className="hint-controls">
          <button
            className={`hint-button ${isHintVisible ? 'hint-active' : ''}`}
            onClick={toggleHint}
            type="button"
            aria-label="Show hint for current question"
          >
            Need a hint?
          </button>

          <button
            className={`overall-hint-button ${isOverallHintVisible ? 'hint-active' : ''}`}
            onClick={toggleOverallHint}
            type="button"
            aria-label="Show overall hint for this work"
          >
            Overall Hint
          </button>
        </div>

        {/* Question-specific hint display */}
        {isHintVisible && (
          <div className="hint-box question-hint">
            <div className="hint-header">Hint:</div>
            <div className="hint-content">{getCurrentHint()}</div>
          </div>
        )}

        {/* Work-level hint display */}
        {isOverallHintVisible && (
          <div className="hint-box overall-hint">
            <div className="hint-header">Overall Hint:</div>
            <div className="hint-content">{getOverallHint()}</div>
          </div>
        )}
      </div>

      {/* Progress bar visualization */}
      <div className="game-progress-display">
        <div className="progress-bar">
          <div
            className="progress-fill"
            style={{ width: `${(currentQuestionIndex / (maxQuestions - 1)) * 100}%` }}
          ></div>
        </div>
      </div>
    </div>
  );
};

/**
 * PropTypes validation for component props
 * Ensures proper typing and required props
 */
GamePlay.propTypes = {
  currentWork: PropTypes.object.isRequired,
  questions: PropTypes.array.isRequired,
  currentQuestionIndex: PropTypes.number.isRequired,
  setCurrentQuestionIndex: PropTypes.func.isRequired,
  userAnswers: PropTypes.object.isRequired,
  setUserAnswers: PropTypes.func.isRequired,
  askedQuestions: PropTypes.instanceOf(Set).isRequired,
  setAskedQuestions: PropTypes.func.isRequired,
  inputValue: PropTypes.string.isRequired,
  setInputValue: PropTypes.func.isRequired,
  setGameResult: PropTypes.func.isRequired,
  setGameOver: PropTypes.func.isRequired,
  setDebugInfo: PropTypes.func.isRequired,
  subject: PropTypes.string.isRequired,
  markWorkAsCompleted: PropTypes.func.isRequired,
  setGameFailureReason: PropTypes.func.isRequired
};

export default GamePlay; 